/**
 * Valid top-level domains (TLDs) for domain validation
 * Includes common gTLDs, ccTLDs, and new gTLDs
 */
const VALID_TLDS = [
  // Generic TLDs
  "com",
  "net",
  "org",
  "edu",
  "gov",
  "mil",
  "int",
  // Common ccTLDs
  "us",
  "uk",
  "ca",
  "au",
  "de",
  "fr",
  "jp",
  "cn",
  "in",
  "br",
  "ru",
  // Latin America
  "mx",
  "ar",
  "cl",
  "co",
  "pe",
  "ve",
  "ec",
  "gt",
  "cu",
  "bo",
  "do",
  "hn",
  "py",
  "sv",
  "ni",
  "cr",
  "pa",
  "uy",
  // New gTLDs
  "io",
  "co",
  "ai",
  "app",
  "dev",
  "tech",
  "online",
  "store",
  "site",
  "shop",
  "blog",
  "news",
  "info",
  "biz",
  "pro",
  "name",
  // Regional TLDs
  "eu",
  "asia",
  "africa",
  // European TLDs
  "nl",
  "be",
  "it",
  "es",
  "ch",
  "at",
  "se",
  "no",
  "dk",
  "fi",
  "pl",
  "ie",
  "pt",
  "gr",
  "cat",
  "cz",
  "ro",
  "hu",
  "bg",
  "sk",
  "hr",
  "si",
  "lt",
  "lv",
  "ee",
  "cy",
  "mt",
  "lu",
  "is",
  "li",
  "mc",
  "sm",
  "va",
  "ad",
  "al",
  "ba",
  "by",
  "md",
  "mk",
  "me",
  "rs",
  "ua",
];

/**
 * Extracts the top-level domain from a URL or domain string
 * @param {string} urlOrDomain - URL or domain string
 * @returns {string|null} - The TLD or null if not found
 */
export const extractTLD = (urlOrDomain) => {
  if (!urlOrDomain || typeof urlOrDomain !== "string") {
    return null;
  }

  try {
    let domain = urlOrDomain.trim().toLowerCase();

    // Remove protocol if present
    domain = domain.replace(/^https?:\/\//, "");
    domain = domain.replace(/^www\./, "");

    // Remove path, query string, and hash
    domain = domain.split("/")[0];
    domain = domain.split("?")[0];
    domain = domain.split("#")[0];

    // Remove port if present
    domain = domain.split(":")[0];

    // Extract TLD (last part after the last dot)
    const parts = domain.split(".");
    if (parts.length < 2) {
      return null; // No TLD found
    }

    return parts[parts.length - 1];
  } catch (error) {
    console.error("Error extracting TLD:", error);
    return null;
  }
};

/**
 * Validates if a domain has a valid TLD
 * @param {string} urlOrDomain - URL or domain string to validate
 * @returns {boolean} - True if TLD is valid, false otherwise
 */
export const validateDomainTLD = (urlOrDomain) => {
  const tld = extractTLD(urlOrDomain);

  if (!tld) {
    return false;
  }

  return VALID_TLDS.includes(tld);
};

/**
 * Gets a user-friendly error message for invalid TLD
 * @param {string} urlOrDomain - URL or domain string
 * @returns {string} - Error message
 */
export const getTLDErrorMessage = (urlOrDomain) => {
  const tld = extractTLD(urlOrDomain);

  if (!tld) {
    return "Please enter a valid domain or URL with a proper top-level domain (e.g., .com, .net, .org)";
  }

  return `Invalid top-level domain ".${tld}". Please use a valid TLD like .com, .net, .org, .io, etc.`;
};
